import { FixMeLater } from "types";
import React, { CSSProperties } from "react";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { DISPLAY_CURRENCIES, TIME_RANGE_1_DAY } from "common/constants";
import { selectPortfolioPrecision } from "@shared/slices/portfolio.slice";
import { round } from "@shared/utils/numbers";
import Flex from "common/Boxes/Flex";
import LineChart from "common/Charts/LineChart/LineChart";
import Text from "common/Typography/Text";
import PriceChange from "common/Charts/PriceChange";
import { typography } from "@shared/common/Themes/typography";
import { formatTimeSeriesChartData } from "@shared/utils/charts";

interface AccountTableChartProps {
  data: FixMeLater;
  isPrice?: boolean;
  yValueIndex?: number;
  fontSize?: number;
  chartHeight?: number;
  style?: CSSProperties;
}

const AccountTableChart = ({
  data,
  isPrice,
  yValueIndex,
  fontSize = typography.pxFontSizes.S,
  chartHeight = ACCOUNT_TABLE_CHART_HEIGHT,
  style,
}: AccountTableChartProps) => {
  const appTheme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const chartData = data?.[TIME_RANGE_1_DAY] || [];
  const precisionConfig = useSelector(selectPortfolioPrecision);
  const precision = precisionConfig?.default || 8;
  let localYValueIndex =
    yValueIndex || (displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1);

  const formattedData = formatTimeSeriesChartData(
    chartData,
    localYValueIndex,
    false,
    null,
    precision,
    true
  );

  let change = Array.isArray(data?.change)
    ? data.change[localYValueIndex - 1]
    : data?.change;
  change = round((change || 0) * 100, 2);

  const color = isPrice
    ? change >= 0
      ? appTheme.colors.green
      : appTheme.colors.red
    : appTheme.colors.primaryBlue;

  return (
    <Flex
      $flexDirection="column"
      $justifyContent="center"
      style={{ width: "100%", position: "relative", ...style }}
    >
      <LineChart
        data={formattedData}
        displayCurrency={displayCurrency}
        isMinified={true}
        style={{ flex: "1 1 auto", height: chartHeight }}
        color={color}
      />
      <Text
        as="div"
        $size={typography.fontSizes.S}
        $weight={typography.fontWeights.SEMI_BOLD}
        color={color}
        style={{ display: "flex", alignItems: "center" }}
      >
        {formattedData?.length > 1 && (
          <PriceChange change={change} size={fontSize} isNeutral={!isPrice} />
        )}
      </Text>
    </Flex>
  );
};

export default AccountTableChart;

export const ACCOUNT_TABLE_CHART_HEIGHT = 45;
